import { useEffect, useState, useMemo } from 'react';
import { Modal, Input, Select, Button, Typography, Radio, Row, Col, theme } from 'antd';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useDebounceFn } from 'ahooks';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import { useFormStyles } from '@/styles/formStyles';
import { useCurrencies } from '@/hooks/useCurrencies';
import { locationApi } from '@/services/api/location.api';
import { bankApi } from '@/services/api/bank.api';
import { useErrorHandler } from '@/lib/errors/useErrorHandler';
import { bankAccountFormModalSchema } from '@/schemas/forms';
import type { CountryInfo } from '@/types/api/location';
import type { BankInfo, BranchInfo } from '@/types/api/bank';
import type { BankAccountFormInput } from '@/types/vendors/bankAccount';
import { useVendorTranslation } from '@/hooks/useVendorTranslation';

interface BankAccountFormModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: (bankAccount: BankAccountFormInput) => void | Promise<void>;
  bankAccount?: BankAccountFormInput | null;
  /** When true, displays form in read-only mode (view mode) */
  readOnly?: boolean;
}

const BankAccountFormModal = ({
  visible,
  onClose,
  onSuccess,
  bankAccount,
  readOnly = false,
}: BankAccountFormModalProps) => {
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const { t } = useVendorTranslation();
  const { handleError } = useErrorHandler();
  const { currencyOptions, isLoading: isLoadingCurrencies } = useCurrencies();
  const isEditMode = !!bankAccount;

  // Read-only input style
  const readOnlyStyle = readOnly ? {
    backgroundColor: token.colorBgContainerDisabled,
    cursor: 'default',
  } : {};

  const [countries, setCountries] = useState<CountryInfo[]>([]);
  const [banks, setBanks] = useState<BankInfo[]>([]);
  const [branches, setBranches] = useState<BranchInfo[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [selectedCountryId, setSelectedCountryId] = useState<number | null>(null);
  const [selectedBankId, setSelectedBankId] = useState<number | null>(null);
  const [bankSearchValue, setBankSearchValue] = useState('');
  const [branchSearchValue, setBranchSearchValue] = useState('');
  const [saving, setSaving] = useState(false);

  // Default values for empty/create mode
  const defaultBankAccountValues: BankAccountFormInput = useMemo(
    () => ({
      isDefault: false,
      accountName: '',
      countryId: 0,
      bankLocation: '',
      bankId: undefined,
      bankCode: '',
      bankName: '',
      currency: 'USD',
      accountNumber: '',
      branchId: undefined,
      branchCode: '',
      branchName: '',
      swiftCode: '',
      iban: '',
      notes: '',
      isActive: true,
    }),
    []
  );

  // Compute form values using useMemo (prevents unnecessary recalculations)
  // When modal is visible: use bankAccount data if editing, otherwise use defaults
  // When modal is hidden: return defaults to reset form
  const formValues = useMemo(() => {
    if (!visible) {
      return defaultBankAccountValues; // Reset to defaults when modal closes
    }
    return bankAccount || defaultBankAccountValues;
  }, [visible, bankAccount, defaultBankAccountValues]);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, touchedFields },
  } = useForm<BankAccountFormInput>({
    resolver: zodResolver(bankAccountFormModalSchema),
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: defaultBankAccountValues,
    // Use `values` prop for reactive updates (recommended by RHF docs)
    values: formValues,
  });

  // Reset form when modal opens to ensure clean state
  // This handles the case where switching from edit to add mode
  useEffect(() => {
    if (visible) {
      reset(formValues);
    }
  }, [visible, formValues, reset]);

  // Load countries on mount
  useEffect(() => {
    const loadCountries = async () => {
      setLoadingCountries(true);
      try {
        const response = await locationApi.getCountries();
        setCountries(response.data);
      } catch (error) {
        handleError(error);
      } finally {
        setLoadingCountries(false);
      }
    };

    if (visible) {
      loadCountries();
    }
  }, [visible, handleError]);

  // Search banks based on user input (debounced)
  const searchBanks = async (searchText: string) => {
    if (!selectedCountryId || !searchText || searchText.trim().length === 0) {
      setBanks([]);
      return;
    }

    const trimmedText = searchText.trim();
    setLoadingBanks(true);
    try {
      // If input is all numbers, search by bank code; otherwise search by bank name
      const isNumeric = /^\d+$/.test(trimmedText);
      const response = await bankApi.searchBanks({
        countryId: selectedCountryId,
        ...(isNumeric ? { code: trimmedText } : { bankName: trimmedText }),
      });
      setBanks(response.data);
    } catch (error) {
      console.error('Error searching banks:', error);
      setBanks([]);
    } finally {
      setLoadingBanks(false);
    }
  };

  // Debounced bank search
  const { run: debouncedBankSearch } = useDebounceFn(searchBanks, {
    wait: 500,
  });

  // Search branches based on user input (debounced)
  const searchBranches = async (searchText: string) => {
    if (!selectedCountryId || !selectedBankId) {
      setBranches([]);
      return;
    }

    // If search text is empty or too short, don't search yet
    if (!searchText || searchText.trim().length < 1) {
      setBranches([]);
      return;
    }

    setLoadingBranches(true);
    try {
      // Search by branch name (supports fuzzy search)
      const response = await bankApi.searchBranches({
        countryId: selectedCountryId,
        bankId: selectedBankId,
        branchName: searchText.trim(),
      });
      setBranches(response.data);
    } catch (error) {
      console.error('Error searching branches:', error);
      setBranches([]);
    } finally {
      setLoadingBranches(false);
    }
  };

  // Debounced branch search
  const { run: debouncedBranchSearch } = useDebounceFn(searchBranches, {
    wait: 500,
  });

  // Initialize local state when modal opens with bank account data
  // Form values are handled by the `values` prop, this only handles local state
  useEffect(() => {
    if (visible && bankAccount && countries.length > 0) {
      // Set country ID directly if available, otherwise find by name
      if (bankAccount.countryId) {
        setSelectedCountryId(bankAccount.countryId);
      } else {
        // Fallback: find country ID from bank location name (for legacy data)
        const country = countries.find((c) => c.countryName === bankAccount.bankLocation);
        if (country) {
          setSelectedCountryId(country.countryId);
          setValue('countryId', country.countryId);
        }
      }
      // Set bank ID if available (needed for branch search)
      if (bankAccount.bankId) {
        setSelectedBankId(bankAccount.bankId);
      }
      // In edit mode, show bankCode in search input when bankId (lookup ID) is not available
      // This allows the user to see the existing bank code value
      if (bankAccount.bankCode && !bankAccount.bankId) {
        setBankSearchValue(bankAccount.bankCode);
      }
      // Similarly for branch - show the branch name/code when branchId is not available
      if ((bankAccount.branchName || bankAccount.branchCode) && !bankAccount.branchId) {
        setBranchSearchValue(bankAccount.branchName || bankAccount.branchCode || '');
      }
    } else if (visible && !bankAccount) {
      // Reset local state for create mode
      setSelectedCountryId(null);
      setSelectedBankId(null);
      setBankSearchValue('');
      setBranchSearchValue('');
    } else if (!visible) {
      // Reset local state when modal closes
      setSelectedCountryId(null);
      setSelectedBankId(null);
      setBankSearchValue('');
      setBranchSearchValue('');
    }
  }, [visible, bankAccount, countries, setValue]);

  const onSubmit = async (data: BankAccountFormInput) => {
    setSaving(true);
    try {
      await onSuccess(data);
      reset();
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  const handleCountryChange = (value: string) => {
    const country = countries.find((c) => c.countryName === value);
    if (country) {
      setSelectedCountryId(country.countryId);
      setValue('countryId', country.countryId); // Set countryId for API
      setValue('bankLocation', country.countryName); // Set display name
      // Reset bank and branch when country changes
      setSelectedBankId(null);
      setBanks([]);
      setBankSearchValue('');
      setBranches([]);
      setBranchSearchValue('');
      setValue('bankId', undefined);
      setValue('bankCode', '');
      setValue('bankName', '');
      setValue('branchId', undefined);
      setValue('branchCode', '');
      setValue('branchName', '');
    }
  };

  // Use standardized form styles
  const inputStyle = formStyles.inputStyle;

  // Custom styles (extend formStyles)
  const inputStyleError = {
    ...formStyles.inputStyle,
    borderColor: token.colorError,
  };

  const _readOnlyInputStyle = {
    ...formStyles.inputStyle,
    backgroundColor: token.colorBgContainer,
    cursor: 'default',
    color: token.colorText,
    borderColor: token.colorBorder,
  };

  const fieldLabelStyle = {
    fontSize: token.fontSizeSM,
    color: token.colorTextSecondary,
  };

  // Footer buttons - show only Close button in read-only mode
  const footerButtons = readOnly ? (
    <div className="tw-flex tw-justify-end">
      <Button
        onClick={onClose}
        style={{
          height: `${controlHeightTokens.controlHeightXL}px`,
          fontSize: token.fontSizeSM,
          borderRadius: `${radiusTokens.card}px`,
        }}
      >
        {t('vendor.actions.close')}
      </Button>
    </div>
  ) : (
    <div className="tw-flex tw-justify-end tw-gap-2">
      <Button
        danger
        onClick={handleCancel}
        style={{
          height: `${controlHeightTokens.controlHeightXL}px`,
          fontSize: token.fontSizeSM,
          borderRadius: `${radiusTokens.card}px`,
        }}
      >
        {t('vendor.actions.cancel')}
      </Button>
      <Button
        type="primary"
        htmlType="submit"
        form="bank-account-form"
        loading={saving}
        style={{
          height: `${controlHeightTokens.controlHeightXL}px`,
          fontSize: token.fontSizeSM,
          borderRadius: `${radiusTokens.card}px`,
        }}
      >
        {isEditMode ? t('vendor.actions.save') : t('vendor.modals.add')}
      </Button>
    </div>
  );

  // Modal title based on mode
  const modalTitle = readOnly
    ? t('vendor.modals.viewBankAccount')
    : isEditMode
      ? t('vendor.modals.editBankAccount')
      : t('vendor.modals.addBankAccount');

  return (
    <Modal
      title={modalTitle}
      open={visible}
      onCancel={handleCancel}
      footer={footerButtons}
      width={900}
      destroyOnHidden
      styles={{
        body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' },
      }}
    >
      <style>
        {`
          .ant-select-dropdown .ant-select-item {
            min-height: ${controlHeightTokens.controlHeightXL}px !important;
            padding: 10px 12px !important;
            font-size: ${token.fontSize}px !important;
            line-height: ${controlHeightTokens.controlHeightXL - 20}px !important;
            display: flex !important;
            align-items: center !important;
          }
        `}
      </style>
      <form
        id="bank-account-form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(onSubmit)(e);
        }}
      >
        {/* Basic Information Container */}
        <div style={formStyles.fieldContainerStyle}>
          <Typography.Text strong style={formStyles.labelStyle}>
            {t('vendor.sections.basicInformation')}
          </Typography.Text>
          <Row gutter={12}>
            <Col span={18}>
              <Typography.Text style={fieldLabelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('vendor.fields.accountName')}
              </Typography.Text>
              <Controller
                name="accountName"
                control={control}
                rules={{ required: t('vendor.validation.accountNumberRequired') }}
                render={({ field }) => (
                  <Input
                    {...field}
                    size="large"
                    readOnly={readOnly}
                    placeholder={t('vendor.placeholders.accountNumber')}
                    style={{
                      ...errors.accountName ? inputStyleError : inputStyle,
                      ...readOnlyStyle,
                      marginTop: '4px',
                    }}
                  />
                )}
              />
              {!readOnly && errors.accountName && touchedFields.accountName && (
                <Typography.Text
                  style={{
                    fontSize: token.fontSizeSM,
                    color: token.colorError,
                  }}
                >
                  {t(errors.accountName?.message ?? '')}
                </Typography.Text>
              )}
            </Col>
            <Col span={6}>
              <Typography.Text style={fieldLabelStyle}>
                {t('vendor.fields.default')}
              </Typography.Text>
              <Controller
                name="isDefault"
                control={control}
                render={({ field }) => (
                  <Radio.Group
                    {...field}
                    value={field.value}
                    onChange={(e) => !readOnly && field.onChange(e.target.value)}
                    className={readOnly ? 'form-readonly-radio' : undefined}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      height: `${controlHeightTokens.controlHeightXL}px`,
                      marginTop: '4px',
                    }}
                  >
                    <Radio value={true}>{t('vendor.fields.yes')}</Radio>
                    <Radio value={false}>{t('vendor.fields.no')}</Radio>
                  </Radio.Group>
                )}
              />
            </Col>
          </Row>
        </div>

        {/* Bank Details Container */}
        <div style={formStyles.fieldContainerStyle}>
          <Typography.Text strong style={formStyles.labelStyle}>
            {t('vendor.sections.bankDetails')}
          </Typography.Text>

          {/* Bank Location */}
          <div>
            <Typography.Text style={fieldLabelStyle}>
              <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
              {t('vendor.fields.bankLocation')}
            </Typography.Text>
            <Controller
              name="bankLocation"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  value={field.value}
                  size="large"
                  showSearch={!readOnly}
                  open={readOnly ? false : undefined}
                  className={readOnly ? 'form-readonly-select' : undefined}
                  placeholder={t('vendor.placeholders.country')}
                  loading={loadingCountries}
                  onChange={(value) => {
                    if (readOnly) return;
                    field.onChange(value);
                    handleCountryChange(value);
                  }}
                  options={countries.map((c) => ({
                    label: c.countryName,
                    value: c.countryName,
                  }))}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  style={{
                    ...(errors.bankLocation ? inputStyleError : inputStyle),
                    marginTop: '4px',
                  }}
                />
              )}
            />
            {!readOnly && errors.bankLocation && touchedFields.bankLocation && (
              <Typography.Text
                style={{
                  fontSize: token.fontSizeSM,
                  color: token.colorError,
                }}
              >
                {t(errors.bankLocation?.message ?? '')}
              </Typography.Text>
            )}
          </div>

          {/* Bank Code + Bank Name */}
          <Row gutter={12} style={{ marginTop: '16px' }}>
            <Col span={12}>
              <Typography.Text style={fieldLabelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('vendor.fields.bankCode')}
              </Typography.Text>
              {readOnly ? (
                // In view mode, show bankCode as plain text input
                <Controller
                  name="bankCode"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      size="large"
                      readOnly
                      style={{
                        ...inputStyle,
                        ...readOnlyStyle,
                        marginTop: '4px',
                      }}
                    />
                  )}
                />
              ) : (
                <Controller
                  name="bankId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={field.value || undefined}
                      size="large"
                      placeholder={t('vendor.placeholders.bankCode')}
                      loading={loadingBanks}
                      disabled={!selectedCountryId}
                      status={errors.bankCode ? 'error' : undefined}
                      showSearch
                      allowClear
                      filterOption={false}
                      searchValue={bankSearchValue}
                      onSearch={(value) => {
                        setBankSearchValue(value);
                        debouncedBankSearch(value);
                        // If user clears the search input (backspace/delete), also clear the form's bankCode
                        // This handles the case when using custom bank codes without a bankId
                        if (!value && !field.value) {
                          setValue('bankCode', '');
                        }
                      }}
                      onChange={(value) => {
                        field.onChange(value);
                        const selectedBank = banks.find((b) => b.bankId === value);
                        if (selectedBank) {
                          setSelectedBankId(selectedBank.bankId);
                          setValue('bankId', selectedBank.bankId);
                          setValue('bankCode', selectedBank.bankCode);
                          // Auto-fill bank name when selecting from dropdown
                          setValue('bankName', selectedBank.name);
                          setBankSearchValue('');
                          // Reset branch when bank changes
                          setBranches([]);
                          setBranchSearchValue('');
                          setValue('branchId', undefined);
                          setValue('branchCode', '');
                          setValue('branchName', '');
                        } else {
                          // Clear bank data when selection is cleared
                          setSelectedBankId(null);
                          setValue('bankId', undefined);
                          setValue('bankCode', '', { shouldTouch: true, shouldValidate: true });
                          setValue('bankName', '');
                          setBankSearchValue(''); // Also clear search value so onBlur doesn't restore old value
                          setBranches([]);
                          setBranchSearchValue('');
                          setValue('branchId', undefined);
                          setValue('branchCode', '');
                          setValue('branchName', '');
                        }
                      }}
                      onBlur={() => {
                        const currentBankCode = watch('bankCode');
                        const hasBankIdSelected = !!watch('bankId');

                        if (bankSearchValue) {
                          // If user typed a custom value that doesn't match any bank, keep it as custom input
                          if (bankSearchValue !== currentBankCode && !banks.find((b) => b.bankCode === bankSearchValue)) {
                            setValue('bankCode', bankSearchValue, { shouldTouch: true, shouldValidate: true });
                            setValue('bankId', undefined);
                            setSelectedBankId(null);
                            // Keep bankSearchValue visible as the display value
                          }
                        } else if (!hasBankIdSelected && currentBankCode) {
                          // If search value is empty, no bank selected, but bankCode still has value - clear it
                          setValue('bankCode', '', { shouldTouch: true, shouldValidate: true });
                        } else if (!hasBankIdSelected && !currentBankCode) {
                          // Trigger validation when field is blurred with no value
                          setValue('bankCode', '', { shouldTouch: true, shouldValidate: true });
                        }
                      }}
                      notFoundContent={
                        loadingBanks ? (
                          <div style={{ padding: '8px', textAlign: 'center' }}>{t('vendor.bankAccounts.loading')}</div>
                        ) : bankSearchValue ? (
                          <div
                            style={{
                              padding: '8px',
                              textAlign: 'center',
                              color: token.colorTextSecondary,
                              cursor: 'pointer',
                            }}
                            onClick={() => {
                              // Allow user to use the custom bank code they typed
                              setValue('bankCode', bankSearchValue);
                              setValue('bankId', undefined);
                              setSelectedBankId(null);
                              // Don't clear bankSearchValue - keep it visible as the display value
                              setBanks([]);
                            }}
                          >
                            {t('vendor.bankAccounts.useCustomCode', { code: bankSearchValue })}
                          </div>
                        ) : (
                          <div
                            style={{
                              padding: '8px',
                              textAlign: 'center',
                              color: token.colorTextSecondary,
                            }}
                          >
                            {t('vendor.bankAccounts.typeBankCode')}
                          </div>
                        )
                      }
                      options={banks.map((b) => ({
                        label: `${b.bankCode} - ${b.name}`,
                        value: b.bankId, // Use bankId as unique value to avoid duplicate key warnings
                        bankCode: b.bankCode, // Store bankCode for labelRender
                      }))}
                      labelRender={({ value }) => {
                        // Display only the bank code when selected, not the full label
                        const bank = banks.find((b) => b.bankId === value);
                        // Fallback to stored bankCode when bank not in current search results
                        return bank?.bankCode || watch('bankCode') || '';
                      }}
                      style={{
                        ...(errors.bankCode ? inputStyleError : inputStyle),
                        marginTop: '4px',
                      }}
                    />
                  )}
                />
              )}
              {!readOnly && errors.bankCode && (
                <Typography.Text
                  style={{
                    fontSize: token.fontSizeSM,
                    color: token.colorError,
                  }}
                >
                  {t(errors.bankCode?.message ?? '')}
                </Typography.Text>
              )}
            </Col>
            <Col span={12}>
              <Typography.Text style={fieldLabelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('vendor.fields.bankName')}
              </Typography.Text>
              <Controller
                name="bankName"
                control={control}
                rules={{ required: t('vendor.validation.bankNameRequired') }}
                render={({ field }) => (
                  <Input
                    {...field}
                    size="large"
                    readOnly={readOnly}
                    placeholder={t('vendor.placeholders.enterBankName')}
                    style={{
                      ...(errors.bankName ? inputStyleError : inputStyle),
                      ...readOnlyStyle,
                      marginTop: '4px',
                    }}
                  />
                )}
              />
              {!readOnly && errors.bankName && touchedFields.bankName && (
                <Typography.Text
                  style={{
                    fontSize: token.fontSizeSM,
                    color: token.colorError,
                  }}
                >
                  {t(errors.bankName?.message ?? '')}
                </Typography.Text>
              )}
            </Col>
          </Row>

          {/* Currency */}
          <div style={{ marginTop: '16px' }}>
            <Typography.Text style={fieldLabelStyle}>
              <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
              {t('vendor.fields.currency')}
            </Typography.Text>
            <Controller
              name="currency"
              control={control}
              rules={{ required: t('vendor.validation.currencyRequired') }}
              render={({ field }) => (
                <Select
                  {...field}
                  value={field.value}
                  size="large"
                  open={readOnly ? false : undefined}
                  className={readOnly ? 'form-readonly-select' : undefined}
                  placeholder={t('vendor.placeholders.currency')}
                  loading={isLoadingCurrencies}
                  options={currencyOptions}
                  style={{
                    ...errors.currency ? inputStyleError : inputStyle,
                    marginTop: '4px',
                  }}
                />
              )}
            />
            {!readOnly && errors.currency && touchedFields.currency && (
              <Typography.Text
                style={{
                  fontSize: token.fontSizeSM,
                  color: token.colorError,
                }}
              >
                {t(errors.currency?.message ?? '')}
              </Typography.Text>
            )}
          </div>
        </div>

        {/* Account Information Container */}
        <div style={formStyles.fieldContainerStyle}>
          <Typography.Text strong style={formStyles.labelStyle}>
            {t('vendor.sections.additional')}
          </Typography.Text>

          {/* Account Number + Branch Name */}
          <Row gutter={12}>
            <Col span={12}>
              <Typography.Text style={fieldLabelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('vendor.fields.accountNumber')}
              </Typography.Text>
              <Controller
                name="accountNumber"
                control={control}
                rules={{ required: t('vendor.validation.accountNumberRequired') }}
                render={({ field }) => (
                  <Input
                    {...field}
                    size="large"
                    readOnly={readOnly}
                    placeholder={t('vendor.placeholders.accountNumber')}
                    style={{
                      ...errors.accountNumber ? inputStyleError : inputStyle,
                      ...readOnlyStyle,
                      marginTop: '4px',
                    }}
                  />
                )}
              />
              {!readOnly && errors.accountNumber && touchedFields.accountNumber && (
                <Typography.Text
                  style={{
                    fontSize: token.fontSizeSM,
                    color: token.colorError,
                  }}
                >
                  {t(errors.accountNumber?.message ?? '')}
                </Typography.Text>
              )}
            </Col>
            <Col span={12}>
              <Typography.Text style={fieldLabelStyle}>{t('vendor.fields.branchName')}</Typography.Text>
              {readOnly ? (
                // In view mode, show branchName as plain text input
                <Controller
                  name="branchName"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ''}
                      size="large"
                      readOnly
                      style={{
                        ...inputStyle,
                        ...readOnlyStyle,
                        marginTop: '4px',
                      }}
                    />
                  )}
                />
              ) : (
                <Controller
                  name="branchId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={field.value}
                      size="large"
                      placeholder={t('vendor.placeholders.branchName')}
                      loading={loadingBranches}
                      showSearch
                      allowClear
                      filterOption={false}
                      searchValue={branchSearchValue}
                      onSearch={(value) => {
                        setBranchSearchValue(value);
                        debouncedBranchSearch(value);
                      }}
                      onChange={(value) => {
                        field.onChange(value);
                        const selectedBranch = branches.find((b) => b.branchId === value);
                        if (selectedBranch) {
                          setValue('branchId', selectedBranch.branchId);
                          setValue('branchCode', selectedBranch.branchCode);
                          // Store clean branch name (branchCode is sent as separate field)
                          setValue('branchName', selectedBranch.name);
                          setBranchSearchValue('');
                        } else {
                          // Clear when user clears selection
                          setValue('branchId', undefined);
                          setValue('branchCode', '');
                          setValue('branchName', '');
                          setBranchSearchValue('');
                        }
                      }}
                      notFoundContent={
                        loadingBranches ? (
                          <div style={{ padding: '8px', textAlign: 'center' }}>{t('vendor.bankAccounts.loading')}</div>
                        ) : !selectedBankId ? (
                          <div
                            style={{
                              padding: '8px',
                              textAlign: 'center',
                              color: token.colorTextSecondary,
                            }}
                          >
                            {t('vendor.bankAccounts.selectBankFirst')}
                          </div>
                        ) : branchSearchValue && branchSearchValue.length >= 1 ? (
                          <div
                            style={{
                              padding: '8px',
                              textAlign: 'center',
                              color: token.colorTextSecondary,
                            }}
                          >
                            {t('vendor.bankAccounts.noBranchesFound')}
                          </div>
                        ) : (
                          <div
                            style={{
                              padding: '8px',
                              textAlign: 'center',
                              color: token.colorTextSecondary,
                            }}
                          >
                            {t('vendor.bankAccounts.typeToSearch')}
                          </div>
                        )
                      }
                      options={branches.map((b) => ({
                        label: `${b.branchCode} - ${b.name}`,
                        value: b.branchId, // Use branchId as unique value
                      }))}
                      labelRender={({ value }) => {
                        // Display only the branch name when selected, not the full label
                        const branch = branches.find((b) => b.branchId === value);
                        // Fallback to stored branchName when branch not in current search results
                        return branch?.name || watch('branchName') || '';
                      }}
                      style={{
                        ...inputStyle,
                        marginTop: '4px',
                      }}
                    />
                  )}
                />
              )}
            </Col>
          </Row>

          {/* SWIFT Code + IBAN */}
          <Row gutter={12} style={{ marginTop: '16px' }}>
            <Col span={12}>
              <Typography.Text style={fieldLabelStyle}>{t('vendor.fields.swiftCode')}</Typography.Text>
              <Controller
                name="swiftCode"
                control={control}
                render={({ field }) => (
                  <Input size="large" {...field}
                    readOnly={readOnly}
                    placeholder={t('vendor.placeholders.swiftCode')}
                    style={{
                      ...inputStyle,
                      ...readOnlyStyle,
                      marginTop: '4px',
                    }}
                  />
                )}
              />
            </Col>
            <Col span={12}>
              <Typography.Text style={fieldLabelStyle}>{t('vendor.fields.iban')}</Typography.Text>
              <Controller
                name="iban"
                control={control}
                render={({ field }) => (
                  <Input size="large" {...field}
                    readOnly={readOnly}
                    placeholder={t('vendor.placeholders.iban')}
                    style={{
                      ...inputStyle,
                      ...readOnlyStyle,
                      marginTop: '4px',
                    }}
                  />
                )}
              />
            </Col>
          </Row>

          {/* Notes */}
          <div style={{ marginTop: '16px' }}>
            <Typography.Text style={fieldLabelStyle}>{t('vendor.fields.notes')}</Typography.Text>
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <Input.TextArea size="large" {...field}
                  readOnly={readOnly}
                  rows={3}
                  placeholder={t('vendor.placeholders.notes')}
                  style={{
                    marginTop: '4px',
                    borderRadius: `${radiusTokens.card}px`,
                    ...readOnlyStyle,
                  }}
                />
              )}
            />
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default BankAccountFormModal;
